import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../firebaseConfig';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { FiChevronDown, FiUser, FiLogOut, FiLogIn, FiExternalLink } from 'react-icons/fi';
import './Navbar.css';

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const navRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setActiveDropdown(null);
        setMenuOpen(false);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setActiveDropdown(null);
        setMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const toggleDropdown = (name) => {
    setActiveDropdown((prev) => (prev === name ? null : name));
  };

  const closeAll = () => {
    setMenuOpen(false);
    setActiveDropdown(null);
  };

  const handleServiceLinkClick = (path) => {
    closeAll();
    navigate(path);
  };

  const handleLogout = async () => {
    closeAll();
    await signOut(auth);
    navigate('/');
  };

  const displayName = user?.displayName || (user?.email ? user.email.split('@')[0] : "User");

  const serviceLinks = [
    { path: '/services/artificial-intelligence-intelligent-automation', num: '01', name: 'AI & Intelligent Automation' },
    { path: '/services/gst-reconciliation', num: '02', name: 'GST Reconciliation' },
    { path: '/services/stock-market-data-analysis', num: '03', name: 'Market Intelligence' },
    { path: '/services/bank-narration-intelligence', num: '04', name: 'Bank Narration Intelligence' },
    { path: '/services/dpdp-compliance', num: '05', name: 'DPDP & Data Privacy' },
    { path: '/services/call-centre-customer-service-automation', num: '06', name: 'Call Centre Automation' },
    { path: '/services/digital-marketing-customer-engagement', num: '07', name: 'Digital Marketing' },
    { path: '/services/automation-process-digitisation-mis', num: '08', name: 'Process Automation & MIS' },
  ];

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`} ref={navRef}>
      <div className="logo">
        <Link to="/" onClick={closeAll}>
          <img src="/logo.png" alt="Data Bolta Hai Logo" />
        </Link>
      </div>

      {/* Toggle Button for Mobile */}
      <button
        className={`menu-toggle ${menuOpen ? 'open' : ''}`}
        onClick={() => setMenuOpen(!menuOpen)}
        aria-label="Toggle navigation"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      <ul className={`nav-links ${menuOpen ? 'active' : ''}`}>
        <li>
          <NavLink to="/" onClick={closeAll} end>
            Home
          </NavLink>
        </li>
        <li>
          <NavLink to="/about" onClick={closeAll}>
            About
          </NavLink>
        </li>

        {/* Services Dropdown — accordion on mobile */}
        <li className={`dropdown ${activeDropdown === 'services' ? 'open' : ''}`}>
          {/* Desktop: NavLink + chevron arrow */}
          <div className="services-desktop-link">
            <NavLink to="/services" onClick={closeAll}>Services</NavLink>
            <button
              type="button"
              className="services-chevron-btn-desktop"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleDropdown('services'); }}
              aria-expanded={activeDropdown === 'services'}
              aria-label="Toggle services submenu"
            >
              <FiChevronDown className={`arrow ${activeDropdown === 'services' ? 'rotated' : ''}`} />
            </button>
          </div>
          {/* Mobile: two separate targets — link navigates, button toggles */}
          <div className="services-toggle-row">
            <NavLink to="/services" onClick={closeAll} className="services-main-link">
              Services
            </NavLink>
            <button
              type="button"
              className="services-chevron-btn"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleDropdown('services'); }}
              aria-expanded={activeDropdown === 'services'}
              aria-label="Toggle services submenu"
            >
              <FiChevronDown className={`arrow ${activeDropdown === 'services' ? 'rotated' : ''}`} />
            </button>
          </div>
          <ul
            className={`dropdown-menu services-dropdown-panel ${activeDropdown === 'services' ? 'show' : ''}`}
          >
            {serviceLinks.map((srv) => (
              <li key={srv.path} className="srv-desktop-item">
                <Link to={srv.path} onClick={closeAll} className={location.pathname === srv.path ? 'srv-active' : ''}>
                  <span className="srv-num">{srv.num}</span>
                  <span className="srv-name">{srv.name}</span>
                  <span className="srv-arrow">→</span>
                </Link>
              </li>
            ))}
            {serviceLinks.map((srv) => (
              <li key={srv.path + '-mobile'} className="srv-mobile-item">
                <button
                  type="button"
                  className={`srv-link-btn ${location.pathname === srv.path ? 'srv-active' : ''}`}
                  onClick={() => handleServiceLinkClick(srv.path)}
                >
                  <span className="srv-num">{srv.num}</span>
                  <span className="srv-name">{srv.name}</span>
                  <span className="srv-arrow">→</span>
                </button>
              </li>
            ))}
          </ul>
        </li>

        {/* DBH-AI Link */}
        <li>
          <NavLink to="/dbh-ai" onClick={closeAll}>
            DBH-AI
          </NavLink>
        </li>

        {/* Our Products Dropdown */}
        <li className={`dropdown ${activeDropdown === 'products' ? 'open' : ''}`}>
          <span onClick={() => toggleDropdown('products')}>
            Our Products <FiChevronDown className={`arrow ${activeDropdown === 'products' ? 'rotated' : ''}`} />
          </span>
          <ul className={`dropdown-menu ${activeDropdown === 'products' ? 'show' : ''}`}>
            <li>
              <a href="https://dbhinsight.in/" target="_blank" rel="noopener noreferrer" onClick={closeAll}>
                DBH Insight <FiExternalLink className="external-icon" />
              </a>
            </li>
            <li>
              <a href="https://deeptiart.com/" target="_blank" rel="noopener noreferrer" onClick={closeAll}>
                Deepti Art <FiExternalLink className="external-icon" />
              </a>
            </li>
          </ul>
        </li>

        <li>
          <NavLink to="/media" onClick={closeAll}>Media</NavLink>
        </li>
        <li>
          <NavLink to="/careers" onClick={closeAll}>Careers</NavLink>
        </li>
        <li>
          <NavLink to="/blog" onClick={closeAll}>Blog</NavLink>
        </li>


        {/* Auth State Conditional Rendering */}
        {!authLoading && (
          user ? (
            <li className={`dropdown user-profile-item ${activeDropdown === 'account' ? 'open' : ''}`}>
              <span className="user-profile-trigger" onClick={() => toggleDropdown('account')}>
                <span className="user-nav-icon"><FiUser /></span>
                <span className="user-nav-name">{displayName}</span>
                <FiChevronDown className="arrow" />
              </span>
              <ul className={`dropdown-menu user-dropdown-menu ${activeDropdown === 'account' ? 'show' : ''}`}>
                <li className="user-menu-header">
                  <div className="user-menu-info">
                    <span className="user-menu-title">{displayName}</span>
                    <span className="user-menu-subtitle">{user.email}</span>
                  </div>
                </li>
                <li className="dropdown-divider" />
                <li>
                  <Link to="/my-account" onClick={closeAll}>
                    <FiUser className="item-icon" /> My Account
                  </Link>
                </li>

                <li>
                  <button className="dropdown-logout-btn" onClick={handleLogout}>
                    <FiLogOut className="item-icon" /> Logout
                  </button>
                </li>
              </ul>
            </li>
          ) : (
            <li>
              <NavLink to="/my-account" onClick={closeAll} className="nav-login-link">
                <FiLogIn className="login-icon" /> Login / Sign In
              </NavLink>
            </li>
          )
        )}
      </ul>
    </nav>
  );
};

export default Navbar;